import { createEventProcessor } from '../events/event-processor.js';
import { createSSEConnection } from '../events/sse-connection.js';

/**
 * @typedef {import('../types/jsdoc-types.js').ReactiveControllerHost} ReactiveControllerHost
 */

/**
 * @typedef {() => Promise<string>} GetAccessToken
 */

export class GameEventsController {
  /**
   * @param {ReactiveControllerHost} host
   * @param {{ entityType?: 'game' | 'adventure', storagePrefix?: string }} [options]
   */
  constructor(host, options = {}) {
    this.host = host;
    /** @type {'game' | 'adventure'} */
    this._entityType = options.entityType || 'game';
    this._storagePrefix = options.storagePrefix || 'game';
    /**
     * Spectator mode is set per-connect, not at construction, because
     * the host element typically doesn't have its attributes wired up
     * yet when the controller is created. See connect().
     */
    this._spectator = false;
    this._eventProcessor = null;
    this._sseConnection = null;
    host.addController(this);
  }

  get eventProcessor() {
    return this._eventProcessor;
  }

  /**
   * @param {object} options
   * @param {string} options.entityId
   * @param {string} options.backendUrl
   * @param {GetAccessToken} options.getAccessToken - Async fn invoked per call to fetch a fresh access token. Used for the REST catch-up fetch and each SSE (re)connect, so token rotation across long sessions is transparent.
   * @param {object} options.callbacks
   * @param {object} [options.eventProcessorCallbacks]
   * @param {boolean} [options.spectator] - Read-only staff spectate mode: skip persistent snapshot + REST catch-up; SSE dumps from event 0.
   */
  async connect({ entityId, backendUrl, getAccessToken, callbacks, eventProcessorCallbacks, spectator }) {
    this._spectator = !!spectator;
    const eventProcessor = createEventProcessor({
      // Spectator sessions get their own storage namespace so they
      // don't share/poison cached state with the player view of
      // the same game.
      storagePrefix: this._spectator ? `${this._storagePrefix}-spectate` : this._storagePrefix,
      storageId: entityId,
      ...eventProcessorCallbacks,
    });
    this._eventProcessor = eventProcessor;

    if (this._spectator) {
      // Spectator: skip persistent snapshot + REST catch-up. The SSE
      // will dump every event from the beginning. We still reset the
      // processor so a stale local cache from a previous spectate
      // session doesn't leak into this one.
      await eventProcessor.resetSnapshot();
      this._sseConnection = createSSEConnection('game-spectate', entityId, backendUrl, {
        onEvent: (event) => eventProcessor.handleSSEEvent(event),
        ...callbacks,
      });
      void this._sseConnection.connect(getAccessToken, null);
      return;
    }

    // 1. Load saved snapshot + lastEventId from localStorage.
    await eventProcessor.loadEvents();

    // 2. Catch up to current state via REST. Events are applied via the
    //    state-only path (no animations, sounds, callouts) — we're
    //    rebuilding state for a session the user wasn't watching live.
    //
    //    The catch-up call also returns the server's latestEventId, which
    //    lets us detect divergence — e.g., when the dev DB has been
    //    reset/resynced under us. If our cached lastEventId is higher
    //    than the server's, our localStorage is from a phantom future:
    //    blow it away and refetch from event 0.
    try {
      const fetchSince = async (/** @type {number} */ since) => {
        const url = `${backendUrl}/api/${this._entityType}s/${entityId}/events?since=${since}`;
        // Fetch a fresh access token for every catch-up request so a
        // token that rotated between the host's mount and this call is
        // picked up. authState.getAccessToken transparently refreshes
        // when near expiry / too old (see web/kit/lib/auth.js).
        const accessToken = await getAccessToken();
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          console.error('[game-events] failed to fetch historical events:', response.status);
          return null;
        }
        return /** @type {{events: object[], latestEventId?: number}} */ (await response.json());
      };

      const sinceParam = eventProcessor.lastEventId ?? 0;
      const data = await fetchSince(sinceParam);
      if (data) {
        const serverLatest = typeof data.latestEventId === 'number' ? data.latestEventId : null;
        const clientLast = eventProcessor.lastEventId ?? 0;
        if (serverLatest !== null && clientLast > serverLatest) {
          console.warn(
            `[game-events] cache divergence: client lastEventId=${clientLast} > server latestEventId=${serverLatest} — resetting and refetching`,
          );
          await eventProcessor.resetSnapshot();
          const fresh = await fetchSince(0);
          if (fresh) {
            await eventProcessor.catchUpStateFromEvents(fresh.events || []);
          }
        } else {
          await eventProcessor.catchUpStateFromEvents(data.events || []);
        }
      }
    } catch (err) {
      console.error('[game-events] error fetching historical events:', err);
    }

    // State is now rebuilt from the saved snapshot + REST catch-up (even if
    // catch-up errored, we render whatever we have rather than hang). Signal
    // that the map can be revealed now, decoupled from the live SSE
    // connection below — a slow or failing stream must not pin the loading
    // overlay (see #584).
    callbacks.onCatchUpComplete?.();

    // 3. Open SSE for live events. Pass the post-catchup lastEventId so the
    //    server's gap-fill returns only events that landed in the tiny
    //    window between the REST response and SSE connect (usually zero).
    this._sseConnection = createSSEConnection(this._entityType, entityId, backendUrl, {
      onEvent: (event) => eventProcessor.handleSSEEvent(event),
      ...callbacks,
    });
    void this._sseConnection.connect(getAccessToken, eventProcessor.lastEventId);
  }

  hostConnected() {
    /* connect() called explicitly by host */
  }

  hostDisconnected() {
    this._sseConnection?.disconnect();
    this._sseConnection = null;
    this._eventProcessor = null;
  }
}
